import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  Clock,
  Award,
  Link,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { Responder, BlockchainVerification as BlockchainVerificationType } from '../types';
import { BlockchainManager } from '../utils/blockchainManager';

interface BlockchainVerificationProps {
  responder: Responder;
  onVerificationComplete?: (verification: BlockchainVerificationType) => void;
  className?: string;
}

export const BlockchainVerification: React.FC<BlockchainVerificationProps> = ({
  responder,
  onVerificationComplete,
  className = ''
}) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<string>('pending');
  const [blockchainManager] = useState(() => new BlockchainManager());
  const [networkStatus, setNetworkStatus] = useState<any>(null);
  const [badgeInfo, setBadgeInfo] = useState<any>(null);

  useEffect(() => {
    checkNetworkStatus();
    if (responder.blockchainVerification?.verificationStatus === 'verified') {
      loadBadgeInfo();
    }
  }, [responder]);

  const checkNetworkStatus = async () => {
    const status = await blockchainManager.getNetworkStatus();
    setNetworkStatus(status);
  };

  const loadBadgeInfo = async () => {
    if (responder.blockchainVerification?.credentialHash) {
      const badgeVerification = await blockchainManager.verifyBadgeAuthenticity(
        responder.blockchainVerification.credentialHash
      );
      setBadgeInfo(badgeVerification);
    }
  };

  const handleVerifyCredentials = async () => {
    setIsVerifying(true);
    setVerificationStatus('verifying');

    try {
      const responderData = {
        name: responder.name,
        badgeNumber: responder.badgeNumber || '',
        category: responder.category,
        certifications: responder.certifications?.map(cert => cert.name) || []
      };

      const walletAddress = generateMockWalletAddress();
      
      const verificationResult = await blockchainManager.verifyResponderCredentials(
        responderData,
        walletAddress
      );

      if (verificationResult.success) {
        const verification: BlockchainVerificationType = {
          walletAddress,
          verificationStatus: 'verified',
          transactionId: verificationResult.transactionId,
          verificationDate: new Date(),
          credentialHash: verificationResult.verificationHash
        };

        setVerificationStatus('verified');
        onVerificationComplete?.(verification);

        // Create responder badge
        const badgeResult = await blockchainManager.createResponderBadge({
          name: responder.name,
          badgeNumber: responder.badgeNumber || '',
          category: responder.category,
          skills: responder.skills.map(skill => skill.name),
          certifications: responder.certifications?.map(cert => ({
            name: cert.name,
            issuer: cert.issuer,
            date: cert.issueDate
          })) || []
        });

        if (badgeResult.success) {
          setBadgeInfo({
            badgeId: badgeResult.badgeId,
            nftAssetId: badgeResult.nftAssetId
          });
        }

      } else {
        setVerificationStatus('failed');
        console.error('Verification failed:', verificationResult.error);
      }

    } catch (error) {
      setVerificationStatus('failed');
      console.error('Verification error:', error);
    } finally {
      setIsVerifying(false);
    }
  };

  const generateMockWalletAddress = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';
    let result = '';
    for (let i = 0; i < 58; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const getStatusIcon = () => {
    switch (verificationStatus) {
      case 'verified':
        return <CheckCircle className="w-6 h-6 text-green-600" />;
      case 'failed':
        return <XCircle className="w-6 h-6 text-red-600" />;
      case 'verifying':
        return <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />;
      default:
        return <Clock className="w-6 h-6 text-yellow-600" />;
    }
  };

  const getStatusColor = () => {
    switch (verificationStatus) {
      case 'verified':
        return 'bg-green-50 border-green-200';
      case 'failed':
        return 'bg-red-50 border-red-200';
      case 'verifying':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-yellow-50 border-yellow-200';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-md border ${className}`}>
      <div className="p-4 border-b bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Blockchain Verification</h3>
            <p className="text-sm text-gray-600">Algorand testnet credential verification</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Network Status */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            <Link className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium">Network Status</span>
          </div>
          <div className="flex items-center gap-2">
            {networkStatus?.isConnected ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <XCircle className="w-4 h-4 text-red-600" />
            )}
            <span className="text-sm text-gray-600">
              {networkStatus?.networkName || 'Checking...'}
            </span>
          </div>
        </div>

        {/* Responder Information */}
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">Responder Details</h4>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Name:</span>
              <div className="font-medium">{responder.name}</div>
            </div>
            <div>
              <span className="text-gray-600">Badge Number:</span>
              <div className="font-medium">{responder.badgeNumber || 'N/A'}</div>
            </div>
            <div>
              <span className="text-gray-600">Category:</span>
              <div className="font-medium capitalize">{responder.category}</div>
            </div>
            <div>
              <span className="text-gray-600">Skills:</span>
              <div className="font-medium">{responder.skills.length} certified</div>
            </div>
          </div>
        </div>

        {/* Verification Status */}
        <div className={`p-4 rounded-lg border-2 ${getStatusColor()}`}>
          <div className="flex items-center gap-3 mb-3">
            {getStatusIcon()}
            <div>
              <h4 className="font-medium text-gray-900 capitalize">
                {verificationStatus === 'verifying' ? 'Verifying Credentials' : 
                 verificationStatus === 'verified' ? 'Credentials Verified' :
                 verificationStatus === 'failed' ? 'Verification Failed' : 'Pending Verification'}
              </h4>
              <p className="text-sm text-gray-600">
                {verificationStatus === 'verifying' ? 'Processing blockchain transaction...' :
                 verificationStatus === 'verified' ? 'Credentials successfully verified on blockchain' :
                 verificationStatus === 'failed' ? 'Unable to verify credentials' : 
                 'Click to start verification process'}
              </p>
            </div>
          </div>

          {verificationStatus === 'verified' && responder.blockchainVerification && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Transaction ID:</span>
                <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                  {responder.blockchainVerification.transactionId?.slice(0, 16)}...
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Verification Date:</span>
                <span>{responder.blockchainVerification.verificationDate?.toLocaleDateString()}</span>
              </div>
              {badgeInfo && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Badge NFT ID:</span>
                  <span className="font-mono text-xs">{badgeInfo.nftAssetId}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Digital Badge */}
        {verificationStatus === 'verified' && badgeInfo && (
          <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-3 mb-3">
              <Award className="w-6 h-6 text-green-600" />
              <h4 className="font-medium text-gray-900">Verified Digital Badge</h4>
            </div>
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Badge ID:</span>
                <span className="font-mono text-xs">{badgeInfo.badgeId}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Blockchain Asset:</span>
                <span className="font-mono text-xs">#{badgeInfo.nftAssetId}</span>
              </div>
              <div className="mt-3 p-2 bg-white rounded border">
                <div className="text-xs text-gray-500 mb-1">Verification Hash:</div>
                <div className="font-mono text-xs break-all">
                  {responder.blockchainVerification?.credentialHash}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Button */}
        {verificationStatus === 'pending' && (
          <button
            onClick={handleVerifyCredentials}
            disabled={isVerifying || !networkStatus?.isConnected}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isVerifying ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Shield className="w-5 h-5" />
            )}
            {isVerifying ? 'Verifying on Blockchain...' : 'Verify Credentials'}
          </button>
        )}

        {verificationStatus === 'failed' && (
          <button
            onClick={handleVerifyCredentials}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <AlertTriangle className="w-5 h-5" />
            Retry Verification
          </button>
        )}

        {/* Disclaimer */}
        <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
          <strong>Note:</strong> This is a demonstration using Algorand testnet. 
          In production, this would connect to the mainnet with real credential verification.
        </div>
      </div>
    </div>
  );
};